export function getDueDateClass(dueDate: string | null): string {
  if (!dueDate) return "";

  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);

  const diffTime = dueDay.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "overdue";
  if (diffDays === 1) return "due-tomorrow";
  if (diffDays === 0) return "due-today";
  return "";
}

/**
 * Parses a date-only API value into a Date at local midnight.
 *
 * API date fields (holdDate, slaDate, reprojectionDate) come back without a
 * timezone offset, e.g. "2026-05-14T10:18:55.347336". Passing that straight to
 * `new Date()` makes the browser read it as local time, which shifts the
 * calendar day for anyone behind UTC. Only the date part is meaningful, so we
 * build the Date from its components and drop the time entirely.
 */
export function parseUtcDateOnly(value: string | null): Date | null {
  if (!value) return null;

  const [year, month, day] = value.split("T")[0].split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

/**
 * Serializes a picked calendar date as UTC midnight, e.g. "2026-05-14T00:00:00Z".
 *
 * Built from the local Y/M/D parts rather than `toISOString()`, which would
 * convert a local-midnight Date to the previous day for negative UTC offsets.
 */
export function toUtcMidnightIso(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00Z`;
}

/**
 * Formats a date-only API value as MM/DD/YYYY without constructing a Date,
 * so no timezone conversion can shift the day.
 */
export function formatUtcDateOnly(value: string | null): string {
  if (!value) return "-";

  const [year, month, day] = value.split("T")[0].split("-");
  if (!year || !month || !day) return "-";

  return `${month}/${day}/${year}`;
}

export function formatDateUS(dateString: string): string {
  if (!dateString) return "-";

  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  return new Date(dateStr);
}

export function formatDateToISO(date: Date | null): string | null {
  if (!date) return null;
  return date.toISOString();
}

export function formatDateOnly(date: Date | null): string | null {
  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function formatTimeToHHMMSS(date: Date | null): string | null {
  if (!date) return null;
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

export function formatTime12Hour(time: string | null): string {
  if (!time) return "";
  const parts = time.split(":");
  if (parts.length < 2) return "";
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (isNaN(hour) || isNaN(minute)) return "";
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const displayMinute = minute.toString().padStart(2, "0");
  return `${displayHour.toString().padStart(2, "0")}:${displayMinute} ${period}`;
}

export function getDueDateText(dueDate: string | null): string {
  if (!dueDate) return "";

  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);

  const diffTime = dueDay.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const formattedDate = `${monthNames[due.getMonth()]} ${due.getDate()}`;

  if (diffDays < 0) {
    return `Due ${formattedDate} (Overdue)`;
  } else if (diffDays === 0) {
    return `Due ${formattedDate} (Due Today)`;
  } else if (diffDays === 1) {
    return `Due ${formattedDate} (Due Tomorrow)`;
  } else {
    return `Due ${formattedDate}`;
  }
}
